import { createContext, useContext } from "react";
import { useNavigate } from "react-router";
import { regelVorschlag, useChatAnlegen, useKategorien, usePersonen, useRegelAktivieren } from "../api/abfragen";
import { ApiFehler, fehlerText, planWeg } from "../api/client";
import { ERKLAEREN } from "../api/assistent";
import type { Dokument } from "../api/typen";
import { useToast } from "../design/toastKontext";
import { useGeistOptional } from "../welt/geistKontext";

/** Fokus beim Nachruecken (Verifier W22 R3, F2): verschwindet die erledigte Vorschlagskarte mit dem
 *  Fokus darin, nimmt ihn, wer danach erscheint (der Kopf-Link der naechsten Karte, sonst die Liste
 *  „Zuletzt abgelegt“ oder die Seitenueberschrift). Nur kurz gueltig und nur, wenn der Fokus nicht
 *  inzwischen woanders liegt; sonst bliebe er nach dem Entfernen auf BODY. */
let fokusAbgegeben = -Infinity;
const FOKUS_GILT_MS = 3000;

export function fokusAbgeben(): void {
  fokusAbgegeben = performance.now();
}

export function fokusUebernehmen(ziel: HTMLElement | null | undefined): void {
  if (!ziel || performance.now() - fokusAbgegeben > FOKUS_GILT_MS) return;
  fokusAbgegeben = -Infinity;
  // W82 Web-5h3 (HW9): solange der Gaist an der Stelle des Tipps antwortet oder der Platz der Karte hält (R1: auch Satz am Platz
  // und Randgeist), rollt der Fokus die Seite nicht weg
  if (document.activeElement === null || document.activeElement === document.body) ziel.focus({ preventScroll: document.documentElement.matches("[data-geist-haelt], [data-karte-haelt]") });
}

/** Regel B2 des Servers beim Ablegen als Vorschau (W82 Web-5b, K14; gleiche Tabelle wie `zahlungsbetrag_ergaenzen` in
 *  `server/app/dienste/klassifizierung.py`): genau eine Zahlungsfrist ohne Betrag und keine Zahlungsfrist mit Betrag
 *  bekommt die Rate des Briefs, wenn er eine hat und die Frist ihr Termin ist (F3 = A, Frage 42 A), sonst seinen Betrag; nur wenn der Wert größer als 0 ist. Sonst
 *  bleibt die Liste, wie sie ist (zwei Zahlungsfristen ohne Betrag zählten die Summe doppelt). Andere Arten bleiben ohne.
 *  Beträge als Text mit Punkt („48.90“) wie in der API. */
export function zahlungsbetragVorschau<F extends { art: string; betrag: string | null; datum?: string | null }>(
  fristen: F[],
  betrag: string | null,
  rate: string | null,
  rateAb: string | null = null,
): F[] {
  const zahlungen = fristen.filter((f) => f.art === "zahlen");
  const ohne = zahlungen.filter((f) => !f.betrag);
  if (ohne.length !== 1 || ohne.length !== zahlungen.length) return fristen;
  // Frage 42 A (D-588): die Rate nur, wenn die Frist ihr Termin ist (`rateAb` fehlt oder fällt auf die Frist); davor ist es die
  // Nachzahlung und trägt den Betrag des Briefs. Ohne Betrag bleibt die Rate. Wie `zahlungsbetrag_ergaenzen` im Server.
  const amTermin = !rateAb || ohne[0]?.datum === rateAb;
  const wert = Number((rate !== null && (amTermin || betrag === null) ? rate : betrag) ?? 0);
  if (!(wert > 0) || !Number.isFinite(wert)) return fristen;
  return fristen.map((f) => (f === ohne[0] ? { ...f, betrag: wert.toFixed(2) } : f));
}

/** W82 Web-Antwort-Ablegen (D-772, Frage 104 = A): die Dokumentansicht legt um ihr Blatt „Prüfen“ („Prüfen und ablegen“) die
 *  Antwort des Gaist. Nach dem Ablegen aus dem Blatt fragt die Meldung sie zuerst; true heißt, er antwortet (bei „Überall“ wie
 *  nach „Passt“ der Karte): dann kein Toast „Abgelegt.“, die Frage „Immer so“ ohne Vorsilbe und erst nach dem Ende der Antwort
 *  (HW6, BS28). `neu`: die mit dem Brief abgelegten Fristen, wie `onAbgelegt` der Karte sie nennt. */
export const BlattAntwort = createContext<((abgelegt: Dokument, neu: { datum: string }[]) => boolean) | null>(null);

/** Nach dem Ablegen (Formular oder „Passt“ der Vorschlagskarte): „Abgelegt.“ und, solange die Regel
 *  zum Absender noch nicht von selbst gilt, die Frage „Immer so“ (W15). `still`: die Vorschlagskarte
 *  meldet das Ablegen selbst („Abgelegt in …“, Plan 6.1), eine Meldung reicht; die Frage bleibt, ohne das vorangestellte
 *  „Abgelegt.“ (BS28: die Blase des Geists sagt es schon). `fristen`: die mit diesem Ablegen gesendeten Fristen (das Blatt
 *  „Prüfen“ übergibt sie aus seinem Auftrag, W82 Web-6b WN-16); die Antwort des Gaist nennt sie. */
export function useAbgelegtMelden(): (abgelegt: Dokument, still?: boolean, fristen?: readonly { datum: string }[]) => void {
  const toast = useToast();
  const blattAntwort = useContext(BlattAntwort);
  const { data: kategorien } = useKategorien();
  const { data: personen } = usePersonen();
  const regelAktivieren = useRegelAktivieren();
  // W82 Web-5h3 (HW6, D-655): nie zwei Meldungen zugleich; steht eine Antwort des Gaist, kommt die Frage erst nach „Danke“
  const geist = useGeistOptional();
  const danach = (tun: () => void) => (geist ? geist.nachAntwort(tun) : tun());
  return (abgelegt, still = false, fristen = []) => {
    // D-772: antwortet der Gaist nach dem Blatt, sagt seine Antwort „Abgelegt.“ (wie nach „Passt“)
    if (!still && blattAntwort?.(abgelegt, [...fristen])) still = true;
    if (!still) toast("Abgelegt.");
    void regelVorschlag(abgelegt).then((regel) => {
      if (!regel) return;
      const kategorie = kategorien?.find((k) => k.id === regel.kategorie_id)?.name;
      if (!kategorie) return;
      // die Regel traegt auch die Person: sie gehoert in die Frage (Verifier R10)
      const person = personen?.find((p) => p.id === regel.person_id)?.name;
      const wohin = person ? `${kategorie} für ${person}` : kategorie;
      // W82 Web-5f (BS28, K42, D-586): nach „Passt“ (still) spricht nur die Blase „Abgelegt.“; die Frage beginnt dann ohne
      danach(() => toast(`${still ? "" : "Abgelegt. "}Post von ${regel.absender} immer unter ${wohin}?`, {
        text: "Immer so",
        // mutateAsync: das Formular ist dann schon geschlossen, Rueckrufe an mutate kaemen nicht mehr
        tun: () =>
          void regelAktivieren.mutateAsync(regel).then(
            () => toast(`Post von ${regel.absender} kommt ab jetzt unter ${wohin}.`),
            // 409: die Regel meint inzwischen etwas anderes; der Server sagt, was zu tun ist
            (fehler: unknown) => toast(fehler instanceof ApiFehler && fehler.status === 409 ? fehler.message : "Die Regel konnte nicht gespeichert werden."),
          ),
      }));
    });
  };
}

/** „Brief erklären“: neuer Chat zum Brief, dann weiter zum Gaist mit der Frage. */
export function useErklaeren(): { erklaeren: (dokumentId: string) => void; laeuft: boolean } {
  const chatAnlegen = useChatAnlegen();
  const navigieren = useNavigate();
  const toast = useToast();
  return {
    laeuft: chatAnlegen.isPending,
    erklaeren: (dokumentId) =>
      chatAnlegen.mutate(dokumentId, {
        onSuccess: (chat) => void navigieren(`/assistent/${chat.id}`, { state: { frage: ERKLAEREN } }),
        onError: (e) => toast(fehlerText(e), planWeg(e)),
      }),
  };
}
